import {
  FIELD_AUDIENCES_KEY,
  FIELD_POST_UNTIL_CHANGE_REASON_KEY,
  FIELD_POST_UNTIL_KEY,
  FIELD_PRIVACY_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { FormSchemaField, RolesFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { ClearEvent, FillEvent, PasteEvent } from "PFComponents/table";
import { TableInstance } from "PFComponents/table/table.types";
import { RefObject, useCallback } from "react";
import { useFormContext } from "react-hook-form";

import { getClearValue, parseValue } from "../tabular_roles_form.utils";
import { CellBehavior } from "./use_get_cell_behavior";

type TableRowInfo = {
  uniqueId: string;
  disabled: boolean;
};

type UseInteractiveFormHandlersProps = {
  tableInstanceRef: RefObject<TableInstance<any> | null>;
  rolesFormKey: RolesFormKey;
  tableRowsInfo: TableRowInfo[];
  getFieldPath: (rowIndex: number, colIndex: number) => string | null;
  getFieldByColumnId: (colId: string) => FormSchemaField | undefined;
  getCellBehavior: (rowOriginal: { uniqueId: string }, schemaField: FormSchemaField) => CellBehavior;
};

export const useInteractiveFormHandlers = ({
  tableInstanceRef,
  rolesFormKey,
  tableRowsInfo,
  getFieldPath,
  getFieldByColumnId,
  getCellBehavior
}: UseInteractiveFormHandlersProps) => {
  const { getValues, setValue } = useFormContext();

  const handleFill = useCallback(
    ({ sourceCell, targetRange }: FillEvent) => {
      const sourcePath = getFieldPath(sourceCell.rowIndex, sourceCell.colIndex);
      if (!sourcePath) {
        return;
      }

      const col = tableInstanceRef.current?.getVisibleLeafColumns()[sourceCell.colIndex];
      const isPrivacyField = col?.id === FIELD_PRIVACY_KEY;
      const isPostUntilField = col?.id === FIELD_POST_UNTIL_KEY;
      const sourceValue = getValues(sourcePath as any);

      const sourceAudiences = isPrivacyField
        ? getValues(`${rolesFormKey}.${sourceCell.rowIndex}.${FIELD_AUDIENCES_KEY}` as any)
        : undefined;

      const sourcePostUntilChangeReason = isPostUntilField
        ? getValues(`${rolesFormKey}.${sourceCell.rowIndex}.${FIELD_POST_UNTIL_CHANGE_REASON_KEY}` as any)
        : undefined;

      for (let row = targetRange.minRow; row <= targetRange.maxRow; row++) {
        if (tableRowsInfo[row]?.disabled) {
          continue;
        }

        const targetPath = getFieldPath(row, sourceCell.colIndex);
        if (!targetPath) {
          continue;
        }

        setValue(targetPath as any, sourceValue, { shouldDirty: true });

        if (isPrivacyField) {
          setValue(`${rolesFormKey}.${row}.${FIELD_AUDIENCES_KEY}` as any, sourceAudiences, {
            shouldDirty: true
          });
        }

        if (isPostUntilField) {
          setValue(
            `${rolesFormKey}.${row}.${FIELD_POST_UNTIL_CHANGE_REASON_KEY}` as any,
            sourcePostUntilChangeReason,
            { shouldDirty: true }
          );
        }
      }
    },
    [getFieldPath, getValues, rolesFormKey, setValue, tableRowsInfo, tableInstanceRef]
  );

  const handlePaste = useCallback(
    ({ anchor, values }: PasteEvent) => {
      const cols = tableInstanceRef.current?.getVisibleLeafColumns() ?? [];
      const rows = tableInstanceRef.current?.getRowModel().rows ?? [];

      values.forEach((row, rowOffset) => {
        const rowIndex = anchor.rowIndex + rowOffset;

        if (!rows[rowIndex] || tableRowsInfo[rowIndex]?.disabled) {
          return;
        }

        const rowOriginal = rows[rowIndex].original;

        row.forEach((rawValue, colOffset) => {
          const colIndex = anchor.colIndex + colOffset;
          const col = cols[colIndex];
          if (!col) {
            return;
          }

          const field = getFieldByColumnId(col.id);
          if (!field) {
            return;
          }

          const { isCopyPasteBlocked, onPaste } = getCellBehavior(rowOriginal, field);
          if (isCopyPasteBlocked) {
            return;
          }

          const path = getFieldPath(rowIndex, colIndex);
          if (!path) {
            return;
          }

          if (onPaste) {
            onPaste(rowOriginal, rowIndex, rawValue, path);
          } else {
            setValue(path as any, parseValue(field, rawValue), { shouldDirty: true });
          }
        });
      });
    },
    [getFieldPath, getFieldByColumnId, getCellBehavior, setValue, tableRowsInfo, tableInstanceRef]
  );

  const handleClear = useCallback(
    ({ targetRange }: ClearEvent) => {
      const cols = tableInstanceRef.current?.getVisibleLeafColumns() ?? [];

      for (let row = targetRange.minRow; row <= targetRange.maxRow; row++) {
        if (tableRowsInfo[row]?.disabled) {
          continue;
        }

        const rowOriginal = tableInstanceRef.current?.getRowModel().rows[row]?.original;

        for (let col = targetRange.minCol; col <= targetRange.maxCol; col++) {
          const colId = cols[col]?.id;
          const field = colId ? getFieldByColumnId(colId) : undefined;
          if (!field) {
            continue;
          }

          const { isClearBlocked, onClear } = getCellBehavior(rowOriginal, field);
          if (isClearBlocked) {
            continue;
          }

          if (onClear) {
            onClear(rowOriginal, row);
            continue;
          }

          const path = getFieldPath(row, col);
          if (path) {
            setValue(path as any, getClearValue(field), { shouldDirty: true });
          }
        }
      }
    },
    [getFieldPath, getFieldByColumnId, getCellBehavior, setValue, tableRowsInfo, tableInstanceRef]
  );

  return { handleFill, handlePaste, handleClear };
};
